import axios from 'axios'
import http from './http'
import { getToken, logout } from './auth'
import i18n from '../i18n/i18n'
import { translateApiMessage } from '../i18n/apiMessages'

const base = 'ai'

const authConfig = () => ({
    headers: {
        'Authorization': 'Bearer ' + getToken()
    }
})

// All API responses are wrapped by the backend as { code, name, message }.
// Callers should check `code === 200` and read the payload from `message`.

// Ask a question. Pass conversationId to continue an existing chat, or omit to
// start a new one. On success `message` is { conversationId, answer }.
export async function ask(question, conversationId) {
    const { data } = await http.post(`${base}/ask`, { question, conversationId }, authConfig())
    return data
}

export async function getConversations() {
    const { data } = await http.get(`${base}/conversations`, authConfig())
    return data
}

export async function getConversation(id) {
    const { data } = await http.get(`${base}/conversations/${id}`, authConfig())
    return data
}

export async function deleteConversation(id) {
    const { data } = await http.delete(`${base}/conversations/${id}`, authConfig())
    return data
}

export async function extractImage(file) {
    const { data } = await http.post(`${base}/extract-image`, file, {
        headers: {
            'Content-Type': file.type,
            'Authorization': 'Bearer ' + getToken(),
        }
    })
    return data
}

// Stream an answer token-by-token over Server-Sent Events. Handlers:
//   onMeta({conversationId}) | onStatus(label) | onToken(text) | onDone({conversationId}) | onError(message)
// onStatus fires while a data tool runs (e.g. "Checking unpaid invoices…").
export async function askStream(question, conversationId, handlers = {}) {
    const { onMeta, onStatus, onToken, onDone, onError } = handlers
    const url = `${axios.defaults.baseURL || ''}/${base}/ask/stream`

    let res
    try {
        res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + getToken(),
            },
            body: JSON.stringify({ question, conversationId: conversationId || null }),
        })
    } catch (e) {
        onError && onError(i18n.t('askSoroby.assistantUnavailable'))
        return
    }

    if (res.status === 401) { logout(); return }

    const contentType = res.headers.get('content-type') || ''
    if (!res.ok || !res.body || !contentType.includes('text/event-stream')) {
        // Backend returned a normal JSON error (auth / quota / not found).
        let message = i18n.t('askSoroby.error')
        try {
            const data = await res.json()
            if (data && typeof data.message === 'string') message = translateApiMessage(data.message)
        } catch (e) { /* ignore */ }
        onError && onError(message)
        return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        let sep
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sep)
            buffer = buffer.slice(sep + 2)
            const dataLine = rawEvent.split('\n').find((l) => l.startsWith('data:'))
            if (!dataLine) continue
            const payload = dataLine.slice(5).trim()
            if (!payload) continue
            let evt
            try { evt = JSON.parse(payload) } catch (e) { continue }
            if (evt.type === 'token') onToken && onToken(evt.value)
            else if (evt.type === 'status') onStatus && onStatus(translateApiMessage(evt.value))
            else if (evt.type === 'meta') onMeta && onMeta(evt)
            else if (evt.type === 'done') onDone && onDone(evt)
            else if (evt.type === 'error') onError && onError(translateApiMessage(evt.message))
        }
    }
}
